"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGoogleMap = exports.callbackName = exports.GOOGLE_MAP_SCRIPT_URL = exports.GOOGLE_MAP_SCRIPT_ID = void 0;
var _checkoutUtils = require("@ori/checkout-utils");
var _react = require("react");
var _useClusteredPickupPointsQuery = require("../api/useClusteredPickupPointsQuery");
var _shared = require("../components/shared");
var _contexts = require("../contexts");
var _logger = require("../logger");
var _utils = require("../utils");
var _useForceRedraw = require("./useForceRedraw");
var _usePickupPointsSearchConfiguration = require("./usePickupPointsSearchConfiguration");
const GOOGLE_MAP_SCRIPT_ID = exports.GOOGLE_MAP_SCRIPT_ID = 'google-map-loader-script';
const GOOGLE_MAP_SCRIPT_URL = exports.GOOGLE_MAP_SCRIPT_URL = 'https://maps.googleapis.com/maps/api/js';
const hookName = 'useGoogleMap';
const callbackName = exports.callbackName = 'updateMap';
const useGoogleMap = props => {
  const {
    account: {
      credentials
    },
    activeFilter,
    currentLocation,
    defaultMapContainer,
    fallbackStartingLocation: initialLocation,
    initialZoom,
    instantSearchTerm,
    isMapVisible,
    minZoom,
    onMapInitialization,
    onPickupPointItemSelection,
    selectedPickupPoint
  } = props;
  const [isMapLoading, setIsMapLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)(undefined);
  const map = (0, _react.useRef)(null);
  const mapContainer = (0, _react.useRef)(defaultMapContainer ?? null);
  // Holding the script load promise to be sure some calls are not duplicated
  // while some re-renders happen.
  const loadingScriptPromise = (0, _react.useRef)(undefined);
  // Storing the loaded script state in global config, as the script callback will be called
  // only once & the initialization of map wouldn't work when returning to the map tab
  // repeatedly.
  const {
    isExternalMapLoaded
  } = (0, _contexts.useCheckoutDeliveryContext)();
  const {
    mapMethods,
    setViewSettings
  } = (0, _usePickupPointsSearchConfiguration.usePickupPointsSearchConfiguration)();
  const isMapInitialized = Boolean(typeof google === 'object' && map.current);

  // Store initial map view options in refs. This can be overwritten
  // until the map gets initialized. When initializing, map view will be set once
  // to the settings stored here.
  const startupLocation = (0, _react.useRef)(initialLocation);
  const startupZoom = (0, _react.useRef)(initialZoom);

  // Google map doesn't store current markers' references, so storing them here.
  const markers = (0, _react.useRef)([]);

  // TO-DO: Let's get rid of this if possible.
  const redraw = (0, _useForceRedraw.useForceRedraw)();
  const boundsRect = map.current?.getBounds();
  const northEast = boundsRect?.getNorthEast();
  const southWest = boundsRect?.getSouthWest();
  const pickupPointsQueryOptions = {
    east: northEast?.lng(),
    isActive: isMapVisible,
    filter: activeFilter,
    north: northEast?.lat(),
    searchTerm: instantSearchTerm,
    south: southWest?.lat(),
    west: southWest?.lng(),
    zoom: map.current?.getZoom()
  };
  const {
    clusteredPickupPointsList,
    isClusteredPickupPointsListLoading
  } = (0, _useClusteredPickupPointsQuery.useClusteredPickupPointsQuery)(pickupPointsQueryOptions);
  (0, _logger.logDebug)(`${hookName} hook was called.\n\tProps:`, props, '\n\tLocal vars:', {
    isExternalMapLoaded,
    isMapInitialized,
    isMapLoading,
    isMapVisible,
    mapMethods,
    pickupPointsQueryOptions,
    startupLocation,
    startupZoom
  });
  const getViewBounds = (0, _react.useCallback)(() => {
    if (typeof google !== 'object' || !map.current) {
      (0, _logger.logWarning)(`${hookName}: getViewBounds() should be called after map has been initialized.`);
      return {
        east: 0,
        north: 0,
        south: 0,
        west: 0
      };
    }
    const bounds = map.current.getBounds();
    const ne = bounds?.getNorthEast();
    const north = ne?.lat() ?? 0;
    const east = ne?.lng() ?? 0;
    const sw = bounds?.getSouthWest();
    const south = sw?.lat() ?? 0;
    const west = sw?.lng() ?? 0;
    return {
      east,
      north,
      south,
      west
    };
  }, []);
  const getViewOptions = (0, _react.useCallback)(viewOptions => {
    const {
      location: rawLocation,
      zoom = map.current?.getZoom() ?? startupZoom.current
    } = viewOptions ?? {};
    const currentCenter = map.current?.getCenter();
    const location = rawLocation ?? (currentCenter ? {
      latitude: Number(currentCenter.lat()),
      longitude: Number(currentCenter.lng())
    } : startupLocation.current);
    return {
      location,
      zoom
    };
  }, [map, startupLocation, startupZoom]);
  const updateView = (0, _react.useCallback)(viewOptions => {
    const {
      location,
      zoom
    } = getViewOptions(viewOptions);
    if (typeof google === 'object' && map.current) {
      // update map view immediately
      const center = {
        lat: location.latitude,
        lng: location.longitude
      };
      const settings = {
        center,
        zoom
      };
      (0, _logger.logDebug)(`${hookName}: updating map view, options:`, settings);
      map.current.setOptions(settings);
      setViewSettings(getViewOptions());
      redraw();
    } else {
      // schedule updating map view for later
      startupZoom.current = zoom;
      startupLocation.current = location;
      (0, _logger.logDebug)(`${hookName}: updateView(): new view options stored:`, {
        location,
        zoom
      });
    }
  }, [getViewOptions, redraw, setViewSettings]);
  const setViewBounds = (0, _react.useCallback)((bounds, padding) => {
    if (typeof google !== 'object' || !map.current) {
      (0, _logger.logWarning)(`${hookName}: setViewBounds() should be called after map has been initialized.`);
      return;
    }
    (0, _logger.logDebug)(`${hookName}: setting map view to new bounds, bounds:`, bounds, ', padding:', padding);
    const {
      east,
      north,
      south,
      west
    } = bounds;
    const usedBounds = new google.maps.LatLngBounds();
    usedBounds.extend(new google.maps.LatLng(north, east));
    usedBounds.extend(new google.maps.LatLng(south, west));
    map.current.fitBounds(usedBounds, padding);
  }, [map]);
  const getViewSettings = (0, _react.useCallback)(() => getViewOptions(), [getViewOptions]);

  // define exposed map methods
  const exposeMapMethods = (0, _react.useCallback)(() => {
    const result = {
      getViewBounds,
      getViewSettings,
      setLocationOrZoom: settings => updateView(settings),
      setViewBounds,
      zoomIn: () => updateView({
        zoom: (map.current?.getZoom() ?? initialZoom) + 1
      }),
      zoomOut: () => updateView({
        zoom: (map.current?.getZoom() ?? initialZoom) - 1
      })
    };
    mapMethods.current = result;
    (0, _logger.logDebug)(`${hookName}: exposeMapMethods() called, mapMethods:`, result);
  }, [getViewBounds, getViewSettings, initialZoom, mapMethods, setViewBounds, updateView]);
  const removePushpins = (0, _react.useCallback)(() => {
    if (!(typeof google === 'object' && map.current)) {
      return;
    }
    markers.current.forEach(item => {
      google.maps.event.clearInstanceListeners(item);
      item.setMap(null);
    });
    markers.current = [];
  }, []);
  const updatePushpins = (0, _react.useCallback)(() => {
    if (!(typeof google === 'object' && map.current)) {
      return;
    }
    removePushpins(); // TO-DO: optimization without deleting all?
    (0, _logger.logDebug)(`${hookName}: updatePushpins() was called.\n\tcurrent pushpins data:`, clusteredPickupPointsList);
    clusteredPickupPointsList.forEach(({
      cluster: rawCluster,
      coordinates: {
        latitude,
        longitude
      },
      displayedClusterSize,
      isPickupPoint,
      pickupPoint: rawPickupPoint
    }) => {
      const position = {
        lat: latitude,
        lng: longitude
      };
      let icon;
      let isSelected;
      let clickHandler;
      if (isPickupPoint) {
        const pickupPoint = rawPickupPoint;
        const {
          deliveryChannelId,
          deliveryMethod,
          estimatedDeliveryDate,
          shippingFee
        } = pickupPoint;
        isSelected = deliveryChannelId === selectedPickupPoint?.deliveryChannelId;
        icon = {
          url: (0, _shared.getPickupPointMapIconSvgString)(deliveryMethod, isSelected, true)
        };
        clickHandler = () => {
          (0, _logger.logDebug)('Pickup point icon was clicked. deliveryChannelId', deliveryChannelId);
          onPickupPointItemSelection?.({
            ...pickupPoint,
            coordinates: {
              latitude,
              longitude
            },
            deliveryFee: shippingFee,
            deliveryMethod,
            estimatedDelivery: estimatedDeliveryDate
          });
        };
      } else {
        const cluster = rawCluster;
        const {
          lowerLeft: {
            latitude: south = 0,
            longitude: west = 0
          } = {},
          upperRight: {
            latitude: north = 0,
            longitude: east = 0
          } = {}
        } = cluster;
        isSelected = false;
        icon = {
          url: (0, _shared.getClusterMapIconSvgString)(displayedClusterSize, isSelected, true)
        };
        clickHandler = () => {
          (0, _logger.logDebug)('Cluster icon was clicked.');
          // deselect last selection, if any
          onPickupPointItemSelection?.(undefined);
          // zoom to cluster min - max coords
          setViewBounds({
            east,
            north,
            south,
            west
          }, Math.round(_shared.baseMarkerSize / 2));
        };
      }
      const {
        x,
        y
      } = (0, _shared.getAnchorCoordinates)(isPickupPoint, isSelected);
      icon.anchor = new google.maps.Point(x, y);
      const item = new google.maps.Marker({
        icon,
        position,
        zIndex: isSelected ? 3 : 2
      });
      item.addListener('click', clickHandler);
      item.setMap(map.current);
      markers.current.push(item);
    });
    // TO-DO: same as for Bing maps? later added pushpin icon seems to have lower "z-index"
    if (currentLocation) {
      const {
        x,
        y
      } = (0, _shared.getAnchorCoordinates)(false, false);
      const {
        latitude,
        longitude
      } = currentLocation;
      const item = new google.maps.Marker({
        icon: {
          anchor: new google.maps.Point(x, y),
          url: (0, _shared.getCurrentLocationIconSvgString)(true)
        },
        position: {
          lat: latitude,
          lng: longitude
        },
        zIndex: 1
      });
      item.setMap(map.current);
      markers.current.push(item);
      (0, _logger.logDebug)('Current location marker has been set.');
    }
    (0, _logger.logDebug)('Markers has been set to', markers);
  }, [clusteredPickupPointsList, currentLocation, onPickupPointItemSelection, removePushpins, selectedPickupPoint?.deliveryChannelId, setViewBounds]);
  const updateMap = (0, _react.useCallback)(() => {
    // Works only when the Google Map control script was already loaded & evaluated.
    if (typeof google !== 'object') {
      return;
    }

    // map control instance
    if (!map.current && mapContainer.current) {
      (0, _logger.logDebug)(`${hookName}: Initializing map instance ...`);
      const {
        latitude,
        longitude
      } = startupLocation.current;
      map.current = new google.maps.Map(mapContainer.current, {
        center: {
          lat: latitude,
          lng: longitude
        },
        clickableIcons: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        keyboardShortcuts: false,
        mapTypeControl: false,
        minZoom,
        rotateControl: false,
        scaleControl: false,
        streetViewControl: false,
        zoom: startupZoom.current,
        zoomControl: false
      });
      google.maps.event.addListener(map.current, 'click', () => {
        (0, _logger.logDebug)('Map was clicked.');
        // deselect last selection, if any
        onPickupPointItemSelection?.(undefined);
      });
      google.maps.event.addListener(map.current, 'dragend', () => {
        (0, _logger.logDebug)('Map view was changed (dragend).');
        redraw();
      });
      google.maps.event.addListener(map.current, 'zoom_changed', () => {
        (0, _logger.logDebug)('Map view was changed (zoom_changed).');
        redraw();
      });
      // Mark the scripts loaded state for next map tab activations.
      isExternalMapLoaded.current = true;
      // Expose the generic map methods
      exposeMapMethods();
      // Didn't find any event on Bing map once it is ready, so triggering redraw here
      // to get the pushpins displayed also immediatelly after load.
      // TO-DO: Had to add the timeout here as the markers weren't rendering on 1st render for some unknown reason.
      setTimeout(redraw, 500);
      onMapInitialization?.();
    }

    // Pushpins are rendered into HTML canvas element by the map instance.
    if (map.current) {
      updatePushpins();
    }
  }, [exposeMapMethods, isExternalMapLoaded, minZoom, onMapInitialization, onPickupPointItemSelection, redraw, updatePushpins]);
  const loadMap = (0, _react.useCallback)(async () => {
    if (loadingScriptPromise.current !== undefined) {
      return;
    }
    (0, _logger.logDebug)(`${hookName}: loadMap() was called.`);
    setIsMapLoading(true);
    const scriptUrl = `${GOOGLE_MAP_SCRIPT_URL}?callback=${callbackName}&key=${credentials}`;
    loadingScriptPromise.current = (0, _utils.loadScript)(scriptUrl, GOOGLE_MAP_SCRIPT_ID);
    loadingScriptPromise.current.then(() => {
      (0, _logger.logDebug)(`${hookName}: Map control script was loaded.`);
    }).catch(initializationError => {
      (0, _checkoutUtils.showErrorNotification)();
      const message = String(initializationError);
      if (initializationError instanceof Error) {
        setError(initializationError);
      } else {
        const newError = new Error(message);
        setError(newError);
      }
    }).finally(() => {
      setIsMapLoading(false);
    });
  }, [credentials]);
  (0, _react.useEffect)(() => {
    if (isMapInitialized || isExternalMapLoaded.current) {
      updateMap();
    } else if (isMapVisible) {
      // Prepare global callback.
      window[callbackName] = updateMap;
      // Start loading external map control script when the map tab is visible for the 1st time.
      void loadMap();
    }
  }, [isExternalMapLoaded, isMapInitialized, isMapVisible, loadMap, redraw, updateMap]);
  return {
    error,
    isClusteredPickupPointsListLoading,
    isLoading: isClusteredPickupPointsListLoading || isMapLoading,
    isMapLoading,
    map,
    mapContainer
  };
};
exports.useGoogleMap = useGoogleMap;